import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
    inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbPopover, NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap';
import type {
    AncSearchBarCategory,
    AncSearchBarMode,
    AncSearchBarSize,
    AncSearchBarType
} from './anc-search-bar.models';
import { TranslateModule } from '@ngx-translate/core';

/**
 * Search bar aligned with New Library Figma tokens.
 * Parent owns `value` / `terms` and handles `search` side effects (API, routing, etc.).
 */
@Component({
    selector: 'lib-anc-search-bar',
    imports: [CommonModule, NgbPopoverModule, TranslateModule],
    templateUrl: './anc-search-bar.component.html',
    styleUrls: ['./anc-search-bar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AncSearchBarComponent
    implements AfterViewInit, OnChanges, OnDestroy
{
    /** Current draft query in the text field; parent is the source of truth. */
    @Input() value = '';

    /**
     * Committed search terms (multi mode only).
     * Maps to API `filter[search_pattern][0..n]`.
     */
    @Input() terms: readonly string[] = [];

    /** Placeholder shown in the text field. */
    @Input() placeholder = 'Search';

    /** When true, blocks input and icon interaction. */
    @Input() disabled = false;

    /**
     * Figma type variant. Defaults to `default`.
     * `advance` adds the category selector segment on the left.
     */
    @Input() type: AncSearchBarType = 'default';

    /**
     * Figma size variant. Defaults to `mini`.
     * `default` = 48px, `compact` = 40px, `mini` = 30px (default type) / 32px (advance).
     */
    @Input() size: AncSearchBarSize = 'mini';

    /**
     * `single` replaces the active term on submit.
     * `multi` appends each submit as an indexed `search_pattern` value.
     */
    @Input() mode: AncSearchBarMode = 'single';

    /**
     * Multi mode — max inline term badges before collapsing the rest into "+N"
     * (aligned with `lib-anc-select` `multiBadgeLimit`).
     */
    @Input() termBadgeLimit = 2;

    /** Label shown in the advance-type category segment. */
    @Input() categoryLabel = 'All Categories';

    /**
     * `advance` type only — options for the category selector. When empty the
     * category slot stays the static `categoryLabel` it has always been.
     */
    @Input() categoryItems: readonly AncSearchBarCategory[] = [];

    /** `advance` type only — `value` of the selected category. */
    @Input() categoryValue: string | null = null;

    /** Accessible label for the search input. */
    @Input() ariaLabel = 'Search';

    /** Emits on each input event with the raw draft field value. */
    @Output() valueChange = new EventEmitter<string>();

    /** Emits when committed terms change (add, remove, or clear in multi mode). */
    @Output() termsChange = new EventEmitter<string[]>();

    /** Emits trimmed query on Enter or search icon click when non-empty. */
    @Output() search = new EventEmitter<string>();

    /** Emits when multi mode rejects a duplicate term (parent may show a toast). */
    @Output() duplicateTerm = new EventEmitter<string>();

    /** Emits the `value` of a newly picked category. */
    @Output() categoryChange = new EventEmitter<string>();

    /** Emits when the trailing advance-search affordance is clicked (legacy; UI hidden). */
    @Output() advanceSearchClick = new EventEmitter<void>();

    @ViewChild('searchInput')
    private searchInput?: ElementRef<HTMLInputElement>;

    @ViewChild('searchBar') private searchBarRef?: ElementRef<HTMLDivElement>;
    @ViewChild('termsAnchor') private termsAnchor?: NgbPopover;
    @ViewChild('categoryAnchor') categoryAnchor?: NgbPopover;

    /** Whether the text field currently has focus. */
    focused = false;

    /** Terms rendered as inline badges — first `termBadgeLimit`, then "+N" overflow. */
    visibleTerms: string[] = [];

    /** Terms collapsed into the trailing "+N" overflow badge. */
    overflowTerms: string[] = [];

    /** Current full bar width (px) — the terms popover is sized to match it exactly. */
    barWidth = 0;

    private readonly cdr = inject(ChangeDetectorRef);

    private resizeObserver?: ResizeObserver;

    /** Figma Searching=True — draft input is non-empty. */
    get isSearching(): boolean {
        return !!this.value;
    }

    /** Figma trailing clear (`x`) control — clears draft input only. */
    get showClearControl(): boolean {
        return !!this.value;
    }

    ngAfterViewInit(): void {
        if (this.searchBarRef) {
            this.resizeObserver = new ResizeObserver(() =>
                this.updateVisibleTerms()
            );
            this.resizeObserver.observe(this.searchBarRef.nativeElement);
        }
        this.updateVisibleTerms();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['terms'] || changes['termBadgeLimit'] || changes['mode']) {
            this.updateVisibleTerms();
        }
    }

    ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
    }

    /**
     * Shows the first `termBadgeLimit` committed terms inline; the rest collapse into
     * a trailing "+N" badge that opens the full terms list (matches `lib-anc-select`).
     */
    private updateVisibleTerms(): void {
        this.barWidth =
            this.searchBarRef?.nativeElement.clientWidth ?? this.barWidth;

        if (this.mode !== 'multi' || !this.terms.length) {
            this.visibleTerms = [...this.terms];
            this.overflowTerms = [];
            this.cdr.markForCheck();
            return;
        }

        if (this.terms.length > this.termBadgeLimit) {
            this.visibleTerms = this.terms.slice(0, this.termBadgeLimit);
            this.overflowTerms = this.terms.slice(this.termBadgeLimit);
        } else {
            this.visibleTerms = [...this.terms];
            this.overflowTerms = [];
        }

        this.cdr.markForCheck();
    }

    onInput(event: Event): void {
        if (this.disabled) {
            return;
        }

        const nextValue = (event.target as HTMLInputElement).value;
        this.valueChange.emit(nextValue);
    }

    onEnter(event: Event): void {
        event.preventDefault();
        this.submitSearch();
    }

    onIconClick(event: Event): void {
        event.stopPropagation();
        this.submitSearch();
    }

    onClearClick(event: Event): void {
        event.stopPropagation();

        if (this.disabled) {
            return;
        }

        this.valueChange.emit('');
    }

    /** Removes a single committed term (badge dismiss click). */
    onRemoveTerm(term: string, event?: Event): void {
        event?.stopPropagation();

        if (this.disabled || this.mode !== 'multi') {
            return;
        }

        const nextTerms = this.terms.filter((item) => item !== term);
        this.termsChange.emit(nextTerms);

        if (nextTerms.length === 0) {
            this.termsAnchor?.close();
        }
    }

    /** Toggles the full-terms popover (trigger is the "+N" overflow badge). */
    onOverflowClick(event: Event): void {
        event.stopPropagation();

        if (this.disabled) {
            return;
        }

        this.termsAnchor?.toggle();
    }

    onAdvanceSearchClick(event: Event): void {
        event.stopPropagation();

        if (this.disabled) {
            return;
        }

        this.advanceSearchClick.emit();
    }

    onFocus(): void {
        this.focused = true;
    }

    onBlur(): void {
        this.focused = false;
    }

    /** Moves focus to the search input (e.g. after expand). */
    focusInput(): void {
        this.searchInput?.nativeElement.focus();
    }

    /** Label shown in the category slot — selected item, else the static label. */
    get selectedCategoryLabel(): string {
        const found = this.categoryItems.find(
            (item) => item.value === this.categoryValue
        );
        return found?.label ?? this.categoryLabel;
    }

    onCategorySelect(item: AncSearchBarCategory): void {
        this.categoryAnchor?.close();
        if (item.value === this.categoryValue) return;
        this.categoryChange.emit(item.value);
    }

    trackCategory = (_: number, item: AncSearchBarCategory): string =>
        item.value;

    trackTerm(_index: number, term: string): string {
        return term;
    }

    private submitSearch(): void {
        if (this.disabled) {
            return;
        }

        const trimmed = this.value?.trim() ?? '';
        if (!trimmed) {
            return;
        }

        if (this.mode === 'multi') {
            if (this.terms.includes(trimmed)) {
                this.duplicateTerm.emit(trimmed);
                return;
            }

            const nextTerms = [...this.terms, trimmed];
            this.termsChange.emit(nextTerms);
            this.valueChange.emit('');
            this.search.emit(trimmed);
            return;
        }

        this.search.emit(trimmed);
    }
}
